import React from 'react';
import { useHistory } from 'react-router-dom';
import { CommandGroup, CommandItem } from '@dashboard/components/ui/command';
import { useCommandPalette } from './CommandPaletteContext';
import { trackResultClick } from './telemetry';
import { useModifierOpen } from './useModifierOpen';
import { usePageScreenPath } from './usePageScreenPath';
import { renderWithHighlight } from './highlight';
import type { PaletteResultRow } from './groups/usePaletteSearch';

interface Props {
  heading: string;
  groupKey: string;
  query: string;
  useSearch: (query: string) => { data: PaletteResultRow[]; isLoading: boolean; isError: boolean };
  openInNewTab?: boolean;
}

export function PaletteDataGroup({ heading, groupKey, query, useSearch, openInNewTab }: Props) {
  const history = useHistory();
  const pageScreenPath = usePageScreenPath();
  const { closePalette } = useCommandPalette();
  const { data, isLoading, isError } = useSearch(query);
  const modRef = useModifierOpen();

  if (!query) return null;
  if (isError) return null;
  if (!isLoading && data.length === 0) return null;

  return (
    <CommandGroup heading={heading}>
      {isLoading && (
        <>
          {[0, 1, 2].map(i => (
            <CommandItem key={`sk-${i}`} value={`__skeleton-${i}`} disabled>
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
            </CommandItem>
          ))}
        </>
      )}
      {!isLoading &&
        data.map((row, idx) => (
          <CommandItem
            key={`${row.id}-${idx}`}
            value={`${row.label.replace(/<[^>]+>/g, '')} ${row.subtitle || ''} ${row.id}-${idx}`}
            onSelect={() => {
              trackResultClick({ group: groupKey, rank: idx, queryLength: query.length });
              closePalette();
              if (openInNewTab || modRef.current) {
                window.open(row.path, '_blank', 'noopener,noreferrer');
              } else {
                history.push(pageScreenPath(row.path, row.pageType));
              }
            }}
          >
            <div className="flex min-w-0 items-baseline gap-2">
              <span className="truncate">{renderWithHighlight(row.label, query)}</span>
              {row.subtitle && (
                <span className="truncate text-xs text-muted-foreground">
                  {renderWithHighlight(row.subtitle, query)}
                </span>
              )}
            </div>
          </CommandItem>
        ))}
    </CommandGroup>
  );
}
